/**
 * Username + password auth on top of Supabase.
 *
 * Supabase's password auth is EMAIL-based under the hood, so to give players a pure
 * username (no email, no confirmation) each username is mapped to an internal address
 * that is never shown and never emailed (e.g. "charles" → "charles@<USERNAME_DOMAIN>").
 * Requirements in the Supabase dashboard: enable the Email provider and turn OFF
 * "Confirm email" (Authentication → Providers → Email).
 *
 * Tradeoff: no email-based password reset (there's no real email). Easy to add later
 * by collecting an optional real email on the profile.
 */
import { supabase } from "./supabase";

/** Internal-only domain for the synthetic email. Never shown, never emailed. */
const USERNAME_DOMAIN = "players.spaceadventura.game";

export interface Account {
  id: string;
  username: string;
}

function usernameToEmail(username: string): string {
  return `${username.trim().toLowerCase()}@${USERNAME_DOMAIN}`;
}

function accountFromUser(
  u: { id: string; email?: string | null; user_metadata?: Record<string, unknown> } | null | undefined,
): Account | null {
  if (!u) return null;
  const meta = (u.user_metadata?.username as string | undefined) ?? undefined;
  const fromEmail = u.email ? u.email.split("@")[0] : undefined;
  return { id: u.id, username: meta ?? fromEmail ?? "pilot" };
}

export function validateUsername(username: string): string | null {
  const u = username.trim();
  if (u.length < 3) return "Username must be at least 3 characters.";
  if (u.length > 20) return "Username must be 20 characters or fewer.";
  if (!/^[a-zA-Z0-9_.-]+$/.test(u)) return "Use only letters, numbers, and _ . -";
  return null;
}

export function validatePassword(password: string): string | null {
  if (password.length < 6) return "Password must be at least 6 characters.";
  return null;
}

export async function register(
  username: string,
  password: string,
): Promise<{ account?: Account; error?: string }> {
  if (!supabase) return { error: "Online play isn't set up yet (missing Supabase config)." };
  const vU = validateUsername(username);
  if (vU) return { error: vU };
  const vP = validatePassword(password);
  if (vP) return { error: vP };
  const { data, error } = await supabase.auth.signUp({
    email: usernameToEmail(username),
    password,
    options: { data: { username: username.trim() } },
  });
  if (error) return { error: friendly(error.message) };
  // Duplicate-username guard: when the address already exists, Supabase either errors
  // ("User already registered", caught above) OR — in anti-enumeration mode — returns a
  // fake user with an EMPTY identities array and no error. Treat that empty array as taken.
  const user = data.user as { identities?: unknown[] } | null;
  if (user && Array.isArray(user.identities) && user.identities.length === 0) {
    return { error: "That username is already taken." };
  }
  const account = accountFromUser(data.user);
  if (!account) return { error: "Registration failed — please try again." };
  // Best-effort profile row (a display name for the online list / future leaderboard).
  try {
    await supabase.from("profiles").upsert({ user_id: account.id, display_name: account.username });
  } catch {
    /* not critical for play */
  }
  return { account };
}

export async function login(
  username: string,
  password: string,
): Promise<{ account?: Account; error?: string }> {
  if (!supabase) return { error: "Online play isn't set up yet (missing Supabase config)." };
  const { data, error } = await supabase.auth.signInWithPassword({
    email: usernameToEmail(username),
    password,
  });
  if (error) return { error: friendly(error.message) };
  const account = accountFromUser(data.user);
  if (!account) return { error: "Login failed — please try again." };
  return { account };
}

export async function logout(): Promise<void> {
  await supabase?.auth.signOut();
}

export async function currentAccount(): Promise<Account | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return accountFromUser(data.session?.user);
}

/** Subscribe to login/logout/token changes. Returns an unsubscribe fn. */
export function onAuthChange(cb: (account: Account | null) => void): () => void {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    cb(accountFromUser(session?.user));
  });
  return () => data.subscription.unsubscribe();
}

/** Turn raw Supabase auth errors into player-friendly messages. */
function friendly(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("already registered") || m.includes("already exists") || m.includes("duplicate"))
    return "That username is already taken.";
  if (m.includes("invalid login") || m.includes("invalid credentials"))
    return "Wrong username or password.";
  return message;
}
